import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtDate } from '../../lib/format';
import { Plus, Search, Users } from 'lucide-react';

interface Associado {
  id: string;
  documento: string;
  documentoMask: string;
  nome: string;
  matriculaServidor: string;
  orgaoPublico: string | null;
  situacaoServidor: string | null;
  status: string;
  createdAt: string;
  agente: { id: string; nome: string } | null;
}

const statusColors: Record<string, string> = {
  em_analise: 'bg-amber-100 text-amber-900',
  ativo: 'bg-green-100 text-green-900',
  inativo: 'bg-gray-100 text-gray-700',
  liquidado: 'bg-blue-100 text-blue-900',
  bloqueado: 'bg-red-100 text-red-900',
};

export function AssociadosListaPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['associados', { q: search }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      return api<{ items: Associado[]; total: number; page: number; size: number }>(
        `/associados/?${params.toString()}`,
      );
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Associados</h1>
          <p className="text-muted-foreground">{data?.total ?? 0} cadastros</p>
        </div>
        <Link to="/associados/novo">
          <Button><Plus className="h-4 w-4 mr-2" />Novo associado</Button>
        </Link>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-2 items-center">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, CPF, matrícula ou e-mail…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(q); }}
              className="flex-1"
            />
            <Button variant="secondary" onClick={() => setSearch(q)}>Buscar</Button>
            {search && <Button variant="ghost" size="sm" onClick={() => { setQ(''); setSearch(''); }}>limpar</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <Spinner />}
          {!isLoading && data && data.items.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                {search ? `Nenhum associado encontrado para "${search}"` : 'Nenhum associado cadastrado'}
              </p>
              {!search && (
                <Link to="/associados/novo">
                  <Button size="sm">Cadastrar primeiro associado</Button>
                </Link>
              )}
            </div>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Nome</th>
                    <th className="text-left py-2 pr-3">CPF</th>
                    <th className="text-left py-2 pr-3">Matrícula</th>
                    <th className="text-left py-2 pr-3">Órgão</th>
                    <th className="text-left py-2 pr-3">Agente</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2">Cadastro</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a) => (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 pr-3">
                        <Link to={`/associados/${a.id}`} className="text-primary hover:underline font-medium">
                          {a.nome}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{a.documentoMask}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{a.matriculaServidor}</td>
                      <td className="py-2 pr-3 text-xs">{a.orgaoPublico ?? '—'}</td>
                      <td className="py-2 pr-3 text-xs">{a.agente?.nome ?? '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${statusColors[a.status] ?? 'bg-gray-100'}`}>
                          {a.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-muted-foreground">{fmtDate(a.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
